import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';
import MySnackbar from '../widgets/MySnackbar';
import { AppState } from '../state/MyStateProvider';
import { FirebaseState } from '../firebase/MyFirebaseProvider';

const BasePage = (props) => {
  const {
    componentProps,
    Component,
    titleId,
    AuthenticationRepository,
    applicationName,
    icons,
    commonComponentProps,
  } = props;
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const { state, dispatch } = React.useContext(AppState);
  const { firebaseConfig } = React.useContext(FirebaseState);
  const authRepo = new AuthenticationRepository(state, dispatch, firebaseConfig);

  const queueRef = React.useRef([]);
  const [customPageTitle, setCustomPageTitle] = React.useState(null);
  const [snackbarMessage, setSnackbarMessage] = React.useState(undefined);
  const [isOpenSnackbar, setIsOpenSnackbar] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const processQueue = () => {
    if (queueRef.current.length > 0) {
      setSnackbarMessage(queueRef.current.shift());
      setIsOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsOpenSnackbar(false);
  };

  const handleExitedSnackbar = () => {
    processQueue();
  };

  class PageUtils {
    // eslint-disable-next-line class-methods-use-this
    setPageTitle(title) {
      setCustomPageTitle(title);
    }

    // eslint-disable-next-line class-methods-use-this
    showSnackbar(message, variant) {
      queueRef.current.push({
        message,
        variant,
        key: new Date().getTime(),
      });

      if (isOpenSnackbar) {
        // immediately begin dismissing current message
        // to start one
        setIsOpenSnackbar(false);
      } else {
        processQueue();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    loadSomething(
      load,
      onLoadSuccess,
      successMessage,
      errorMessage,
      shouldShowSuccessSnackbar = true,
      setIsLoadingFalseOnEnd = true,
    ) {
      setIsLoading(true);
      load()
        .then((response) => {
          const { data: responseData } = response;
          const { data } = responseData;
          if (data.success) {
            if (shouldShowSuccessSnackbar) {
              this.showSnackbar(intl.formatMessage({ id: successMessage }), 'success');
            }
            onLoadSuccess(data.payload, response);
          } else if (data.error != null) {
            // console.log(response.data.error);
            if (data.error === 'Auth token is not provided'
            || data.error === 'Token is invalid'
            || data.error === 'Administrator data is invalid') {
              // token is invalid
              authRepo.logout();
              history.push(`/login?redirect=${location.pathname}&type=session_expired`);
            } else {
              this.showSnackbar(`Error: ${data.error}`, 'error');
              if (!setIsLoadingFalseOnEnd) {
                setIsLoading(false);
              }
            }
          } else {
            this.showSnackbar(intl.formatMessage({ id: errorMessage }), 'error');
            if (!setIsLoadingFalseOnEnd) {
              setIsLoading(false);
            }
          }
        })
        .catch((error) => {
          // handle error
          // console.log(error.message);
          this.showSnackbar(
            error.message
              ? error.message
              : intl.formatMessage({ id: errorMessage }),
            'error',
          );
          if (!setIsLoadingFalseOnEnd) {
            setIsLoading(false);
          }
        })
        .finally(() => {
          // always executed
          if (setIsLoadingFalseOnEnd) {
            setIsLoading(false);
          }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadSomethingSilent(
      load,
      onLoadSuccess,
      onLoadFail,
      onLoadFinally,
    ) {
      load()
        .then((response) => {
          // handle success
          const { data: responseData } = response;
          const { data } = responseData;
          if (data.success) {
            onLoadSuccess(data.payload, response);
          } else if (data.error != null) {
            // console.log(response.data.error);
            if (data.error === 'Auth token is not provided'
            || data.error === 'Token is invalid'
            || data.error === 'Administrator data is invalid') {
              // token is invalid
              authRepo.logout();
              history.push(`/login?redirect=${location.pathname}&type=session_expired`);
            } else {
              onLoadFail(data.error, response);
            }
          } else {
            onLoadFail(data.error, response);
          }
        })
        .catch((error) => {
          // handle error
          onLoadFail(error);
        })
        .finally(() => {
          // always executed
          onLoadFinally();
        });
    }

    // eslint-disable-next-line class-methods-use-this
    loadSomethingSilentRaw(
      load,
      onLoadSuccess,
      onLoadFail,
      onLoadFinally,
    ) {
      load()
        .then((response) => {
          // handle success
          const { data } = response;
          onLoadSuccess(data, response);
        })
        .catch((error) => {
          // handle error
          onLoadFail(error);
        })
        .finally(() => {
          // always executed
          onLoadFinally();
        });
    }
  }

  const [pageUtils] = React.useState(new PageUtils());

  // Properties in `commonComponentProps` can be overriden by
  // properties in `componentProps`.
  const finalComponentProps = componentProps || {};
  Object.entries(commonComponentProps).map(([key, value]) => {
    if (!(key in finalComponentProps)) {
      finalComponentProps[key] = value;
    }
    return null;
  });

  return (
    <>
      <Helmet>
        <title>
          {`${customPageTitle || intl.formatMessage({ id: titleId })} | ${applicationName}`}
        </title>
      </Helmet>
      <Component
        pageUtils={pageUtils}
        isLoading={isLoading}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...finalComponentProps}
      />
      <MySnackbar
        key={snackbarMessage ? snackbarMessage.key : undefined}
        open={isOpenSnackbar}
        onClose={handleCloseSnackbar}
        autoHideDuration={6000}
        onExited={handleExitedSnackbar}
        variant={snackbarMessage ? snackbarMessage.variant : undefined}
        message={snackbarMessage ? snackbarMessage.message : ''}
        icons={icons}
      />
    </>
  );
};

BasePage.propTypes = {
  Component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  titleId: PropTypes.string.isRequired,
  AuthenticationRepository: PropTypes.func.isRequired,
  applicationName: PropTypes.string.isRequired,
  icons: PropTypes.objectOf(PropTypes.object).isRequired,
  componentProps: PropTypes.objectOf(PropTypes.any),
  commonComponentProps: PropTypes.objectOf(PropTypes.any),
};

BasePage.defaultProps = {
  componentProps: {},
  commonComponentProps: {},
};

export default BasePage;
